// Country reference data for the enrollment picker and the header flag.
//
// We keep only the ISO 3166-1 alpha-2 codes here and resolve human-readable
// names at runtime via `Intl.DisplayNames`. That avoids hand-maintaining a
// ~200-row name table and gives us localized country names for free once the
// app grows beyond English.

// Comprehensive ISO 3166-1 alpha-2 code set.
export const COUNTRY_CODES = [
  "AD", "AE", "AF", "AG", "AI", "AL", "AM", "AO", "AR", "AS", "AT", "AU", "AW",
  "AX", "AZ", "BA", "BB", "BD", "BE", "BF", "BG", "BH", "BI", "BJ", "BL", "BM",
  "BN", "BO", "BQ", "BR", "BS", "BT", "BW", "BY", "BZ", "CA", "CC", "CD", "CF",
  "CG", "CH", "CI", "CK", "CL", "CM", "CN", "CO", "CR", "CU", "CV", "CW", "CX",
  "CY", "CZ", "DE", "DJ", "DK", "DM", "DO", "DZ", "EC", "EE", "EG", "EH", "ER",
  "ES", "ET", "FI", "FJ", "FK", "FM", "FO", "FR", "GA", "GB", "GD", "GE", "GF",
  "GG", "GH", "GI", "GL", "GM", "GN", "GP", "GQ", "GR", "GT", "GU", "GW", "GY",
  "HK", "HN", "HR", "HT", "HU", "ID", "IE", "IL", "IM", "IN", "IO", "IQ", "IR",
  "IS", "IT", "JE", "JM", "JO", "JP", "KE", "KG", "KH", "KI", "KM", "KN", "KP",
  "KR", "KW", "KY", "KZ", "LA", "LB", "LC", "LI", "LK", "LR", "LS", "LT", "LU",
  "LV", "LY", "MA", "MC", "MD", "ME", "MF", "MG", "MH", "MK", "ML", "MM", "MN",
  "MO", "MP", "MQ", "MR", "MS", "MT", "MU", "MV", "MW", "MX", "MY", "MZ", "NA",
  "NC", "NE", "NF", "NG", "NI", "NL", "NO", "NP", "NR", "NU", "NZ", "OM", "PA",
  "PE", "PF", "PG", "PH", "PK", "PL", "PM", "PN", "PR", "PS", "PT", "PW", "PY",
  "QA", "RE", "RO", "RS", "RU", "RW", "SA", "SB", "SC", "SD", "SE", "SG", "SH",
  "SI", "SJ", "SK", "SL", "SM", "SN", "SO", "SR", "SS", "ST", "SV", "SX", "SY",
  "SZ", "TC", "TD", "TG", "TH", "TJ", "TK", "TL", "TM", "TN", "TO", "TR", "TT",
  "TV", "TW", "TZ", "UA", "UG", "US", "UY", "UZ", "VA", "VC", "VE", "VG", "VI",
  "VN", "VU", "WF", "WS", "YE", "YT", "ZA", "ZM", "ZW",
];

function displayNames(locale) {
  try {
    return new Intl.DisplayNames([locale || "en"], { type: "region" });
  } catch {
    return null;
  }
}

/** Human-readable country name for a code (falls back to the code itself). */
export function getCountryName(code, locale = "en") {
  const cc = String(code || "").toUpperCase();
  if (!cc) return "";
  const dn = displayNames(locale);
  if (dn) {
    try {
      const name = dn.of(cc);
      if (name && name !== cc) return name;
    } catch {
      /* unknown region — fall through */
    }
  }
  return cc;
}

/** Regional-indicator emoji flag (renders as a flag on iOS/Android/macOS). */
export function codeToFlagEmoji(code) {
  const cc = String(code || "").toUpperCase();
  if (cc.length !== 2) return "";
  const BASE = 0x1f1e6; // 🇦
  const a = cc.charCodeAt(0) - 65;
  const b = cc.charCodeAt(1) - 65;
  if (a < 0 || a > 25 || b < 0 || b > 25) return "";
  return String.fromCodePoint(BASE + a, BASE + b);
}

export function isValidCountry(code) {
  return COUNTRY_CODES.includes(String(code || "").toUpperCase());
}

// Name-sorted country list for pickers, memoized per locale.
const _sortedCache = {};
export function getSortedCountries(locale = "en") {
  if (_sortedCache[locale]) return _sortedCache[locale];
  const list = COUNTRY_CODES.map((code) => ({ code, name: getCountryName(code, locale) })).sort(
    (a, b) => a.name.localeCompare(b.name, locale)
  );
  _sortedCache[locale] = list;
  return list;
}

/** Best-effort guess of the user's country from the device locale (e.g. en-US → US). */
export function detectCountryFromLocale() {
  try {
    const langs =
      typeof navigator !== "undefined" && navigator.languages && navigator.languages.length
        ? navigator.languages
        : typeof navigator !== "undefined"
        ? [navigator.language]
        : [];
    for (const lang of langs) {
      const match = /[-_]([A-Za-z]{2})\b/.exec(lang || "");
      if (match) {
        const code = match[1].toUpperCase();
        if (isValidCountry(code)) return code;
      }
    }
  } catch {
    /* non-fatal */
  }
  return "";
}
